/**
 * Payroll — /payroll
 * HR/Payroll operator + super_user only. Three sub-tabs:
 *   1. Current Run — validation, per-therapist calculator, summary entry, finalize
 *   2. Archive — pick any past finalized period; read-only detail table
 *   3. KPI Dashboard — super_user only; margin/session trending
 *   4. Therapist Setup — dialog from the top-right
 *
 * Money is displayed from server-computed cents; the UI never does dollar
 * arithmetic. Server rejects mutations to finalized runs unless super_user
 * calls /override with a reason.
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { Button } from '../components/ui/button';
import { Calculator, Archive, BarChart3, Users, GitCompare } from 'lucide-react';

import { C } from '../lib/payrollHelpers';
import { TherapistSetupDialog } from '../components/payroll/TherapistSetup';
import { CurrentRun } from '../components/payroll/CurrentRun';
import { ArchiveTab } from '../components/payroll/ArchiveTab';
import { KpiDashboard } from '../components/payroll/KpiDashboard';
import { AttendanceChangesTab } from '../components/payroll/AttendanceChangesTab';

export default function Payroll() {
  const { user } = useAuth();
  const { runId: runIdParam } = useParams();
  const nav = useNavigate();
  const isSuper = user?.role === 'super_user';
  const [tab, setTab] = useState(runIdParam ? 'archive' : 'current');
  const [showSetup, setShowSetup] = useState(false);

  return (
    <div className="max-w-[1400px] mx-auto p-5 space-y-4" data-testid="payroll-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: C.gold }}>
            <Calculator size={12} strokeWidth={2.4} /> PAYROLL
          </div>
          <h1 className="font-serif text-[26px]" style={{ color: C.ink }}>Payroll Processing</h1>
          <p className="text-[12px]" style={{ color: C.soft }}>
            Bi-weekly, Thursday → Wednesday · pay rates live in Gusto · every action is audited
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowSetup(true)} data-testid="pr-open-setup">
            <Users size={14} className="mr-1" /> Therapist Setup
          </Button>
        </div>
      </div>

      {/* sub-tabs */}
      <div className="flex items-center gap-1 border-b" style={{ borderColor: C.border }}>
        {[
          { key: 'current', label: 'Current Run', icon: Calculator },
          { key: 'archive', label: 'Archive', icon: Archive },
          { key: 'spchanges', label: 'SP Changes', icon: GitCompare },
          isSuper && { key: 'kpi', label: 'KPI Dashboard', icon: BarChart3 },
        ].filter(Boolean).map(t => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); nav('/payroll', { replace: true }); }}
            data-testid={`pr-tab-${t.key}`}
            className={`px-4 py-2 -mb-px border-b-2 font-semibold text-sm transition-colors ${
              tab === t.key ? 'text-[#2D2833]' : 'text-[#685B72] hover:text-[#2D2833] border-transparent'
            }`}
            style={{ borderColor: tab === t.key ? C.plum : 'transparent' }}
          >
            <t.icon size={14} className="inline mr-1.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'current' && <CurrentRun />}
      {tab === 'archive' && (
        <ArchiveTab
          initialRunId={runIdParam}
          isSuper={isSuper}
          onAmended={(runId) => {
            // Amendment flow (iter 220): after unlocking a finalized run,
            // hop over to Current Run with the run_id in the URL so the
            // OD/super_user lands on the editable draft immediately.
            setTab('current');
            nav(`/payroll?tab=current&run_id=${runId}`, { replace: true });
          }}
        />
      )}
      {tab === 'spchanges' && <AttendanceChangesTab />}
      {tab === 'kpi' && isSuper && <KpiDashboard />}

      {showSetup && <TherapistSetupDialog onClose={() => setShowSetup(false)} />}
    </div>
  );
}
